<template>
    <MainContent
        title="Detalles del equipo"
        :value="filters"
        :meta="deportistas?.meta || {}"
        :loading="!deportistas"
        @input="val => Object.assign(filters, val)"
        @updated="debouncedFetch"
    >
        <template #header>
            <!-- Skeleton del header mientras carga inicial -->
            <template v-if="!deportistas">
                <Skeleton padding="tw:p-0" variant="tw:bg-white tw:shadow-sm tw:rounded-xl tw:border tw:border-gray-200">
                    <template #skeleton>
                        <div class="tw:p-6 tw:flex tw:flex-col tw:gap-4">
                            <!-- SearchComponent skeleton -->
                            <SkeletonItem height="tw:h-10" customClass="tw:w-full tw:rounded-lg" />
                            <div class="tw:border-t tw:border-gray-100"></div>
                            <!-- Grid de filtros: 4 columnas -->
                            <div class="tw:grid tw:grid-cols-2 tw:sm:grid-cols-3 tw:lg:grid-cols-4 tw:gap-3">
                                <div class="tw:space-y-2">
                                    <SkeletonItem height="tw:h-10" customClass="tw:w-full tw:rounded-lg" />
                                </div>
                                <div class="tw:space-y-2">
                                    <SkeletonItem height="tw:h-10" customClass="tw:w-full tw:rounded-lg" />
                                </div>
                                <div class="tw:space-y-2">
                                    <SkeletonItem height="tw:h-10" customClass="tw:w-full tw:rounded-lg" />
                                </div>
                                <div class="tw:flex tw:items-end">
                                    <SkeletonItem height="tw:h-10" customClass="tw:w-32 tw:rounded-lg" />
                                </div>
                            </div>
                        </div>
                    </template>
                </Skeleton>
            </template>
            <Card v-else customClasses="tw:mb-4">
                <div class="tw:flex tw:flex-col tw:gap-4 tw:w-full">
                    <div class="tw:w-full">
                        <SearchComponent
                            :value="filters.search"
                            placeholder="Buscar"
                            @input="val => filters.search = val"
                        />
                    </div>
                    <div class="tw:border-t tw:border-gray-100"></div>
                    <EquiposFilters
                        :filters="filters"
                        :categorias="categorias"
                        :metodos_pago="metodosPagoAbonosBases"
                        @reset="onResetFilters"
                        @update:filters="val => Object.assign(filters, val)"
                    >
                        <template #actions>
                            <Button
                                class="tw:w-full tw:sm:w-auto"
                                icon="fas fa-download"
                                customClass="tw:whitespace-nowrap tw:justify-center"
                                @click="descargarListado"
                            >
                                Descargar listado
                            </Button>
                        </template>
                    </EquiposFilters>
                </div>
            </Card>
        </template>

        <Card customClasses="tw:mb-4">
            <div class="tw:flex tw:flex-col tw:md:flex-row tw:gap-2 tw:md:gap-4 tw:justify-between tw:items-center">
                <h2 class="tw:text-lg tw:font-semibold tw:m-0">Listado de deportistas en el equipo</h2>
                <Button
                    class="tw:w-full tw:md:w-auto tw:justify-center"
                    icon="fas fa-download"
                    @click="descargarListado"
                >
                    Descargar listado
                </Button>
            </div>
        </Card>

        <EquiposDeportistasList
            :deportistas="deportistas?.data || []"
            :loading="loading"
            :skeleton="loading"
            :skeleton-rows="skeletonRows"
            :columns="columns"
            :lista-id="listaId"
        />
    </MainContent>
</template>

<script setup>
    import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
    import MainContent from '@/modules/admin/ui/MainContent.vue';
    import Card from '@/modules/admin/ui/Card.vue';
    import Button from '@/modules/admin/ui/Button.vue';
    import SearchComponent from '@/modules/admin/ui/SearchComponent.vue';
    import EquiposFilters from '@/modules/admin/bases/equipos/components/EquiposFilters.vue';
    import EquiposDeportistasList from '@/modules/admin/bases/equipos/components/EquiposDeportistasList.vue';
    import { Skeleton, SkeletonItem } from '@/modules/admin/ui/skeleton';
    import { useEquiposShow } from '@/composables/admin/bases/equipos/useEquiposShow';
    import { useRoute } from '@/composables/common/router/useRouter';
    import { useToast } from '@/composables/common/useToast';
    import store from '@/store';
    import { config } from '@/config.js';

    const {
        filters,
        deportistas,
        categorias,
        loading,
        listaId,
        metodosPagoAbonosBases,
        debouncedFetch,
        isHydrating,
        updateUrlDebounced,
        init
    } = useEquiposShow();

    const route = useRoute();
    const toast = useToast();

    const equipoId = computed(() => route.params.equipo_id);

    const skeletonRows = computed(() => {
        if (deportistas.value?.data?.length) return deportistas.value.data.length;
        return 3;
    });

    const columns = [
        { key: 'activo', label: 'activo', class: 'tw:w-16', centered: true },
        { key: 'nombre', label: 'Nombre' },
        { key: 'apellidos', label: 'Apellidos', class: 'tw:w-44'},
        { key: 'importe_total', label: 'Importe total' },
        { key: 'importe_pagado', label: 'Importe pagado' },
        { key: 'importe_pendiente', label: 'Importe pendiente'},
        { key: 'acciones', label: 'Opciones', class: 'tw:w-32', centered: true }
    ];

    function onResetFilters() {
        Object.assign(filters.value, {
            search: '',
            search_for: '',
            categoria: '',
            metodo_pago: '',
            page: 1,
            per_page: 10
        });
        debouncedFetch();
    }

    async function descargarListado() {
        try {
            await store.dispatch(
                config.getVuexAlias('back.bases.equipos.descargar-listado'),
                {
                    lista_id: listaId.value,
                    equipo_id: equipoId.value,
                    filters: {
                        tid: localStorage.getItem('hash_teatro')
                    }
                }
            );
            toast.info('Generando listado de equipo');
        } catch (error) {
            toast.error('Error al generar el listado');
        }
    }

    // Catch changes to any filter field (MainContent's @updated only covers page/search/per_page)
    watch(filters, () => {
        if (isHydrating.value) return;
        debouncedFetch();
        updateUrlDebounced();
    }, { deep: true });

    onMounted(() => {
        const queryKeys = Object.keys(route.query);
        if (queryKeys.length > 0) {
            isHydrating.value = true;
            if (route.query.search) filters.value.search = route.query.search;
            if (route.query.search_for) filters.value.search_for = route.query.search_for;
            if (route.query.categoria) filters.value.categoria = route.query.categoria;
            if (route.query.metodo_pago) filters.value.metodo_pago = route.query.metodo_pago;
            if (route.query.page) filters.value.page = parseInt(route.query.page, 10);
            if (route.query.per_page) filters.value.per_page = parseInt(route.query.per_page, 10);
            nextTick(() => {
                isHydrating.value = false;
            });
        }
        init();
    });

</script>
